import { api, API_ENDPOINTS } from '../lib/api';
import { AuthResponse, LoginRequest, RegisterRequest, User } from '../types/auth';

/**
 * Refresh token response from backend
 */
export interface RefreshTokenResponse {
  access_token: string;
  expires_in: number;
}

/**
 * Authentication Service
 * Handles user authentication, registration, and profile management
 */
export const authService = {
  /**
   * Login user with email/phone and password
   * @param credentials - Identifier (email or phone) and password
   * @returns Authentication response with token and user data
   */
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    return response.data;
  },

  /**
   * Register new user
   * @param userData - User registration data
   * @returns Authentication response with token and user data
   */
  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>(API_ENDPOINTS.AUTH.REGISTER, userData);
    return response.data;
  },

  /**
   * Get current user profile
   * Requires authentication token in headers
   * @returns Current user data
   */
  getProfile: async (): Promise<User> => {
    const response = await api.get<User>(API_ENDPOINTS.AUTH.PROFILE);
    return response.data;
  },

  /**
   * Update user profile
   * @param userData - Partial user data to update
   * @returns Updated user data
   */
  updateProfile: async (
    userData: Partial<Omit<User, 'id' | 'role' | 'isActive' | 'createdAt' | 'updatedAt'>>
  ): Promise<User> => {
    const response = await api.patch<User>(API_ENDPOINTS.AUTH.UPDATE_PROFILE, userData);
    return response.data;
  },

  /**
   * Logout user (optional backend call)
   * Note: Actual logout is handled by clearing local storage in auth store
   * This endpoint can be used for server-side session invalidation if needed
   */
  logout: async (): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      // Logout should succeed even if API call fails
      console.warn('Logout API call failed, but local state will be cleared', error);
    }
  },

  /**
   * Refresh access token using refresh token
   * @param refreshToken - The refresh token to exchange for new access token
   * @returns New access token and expiry
   */
  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await api.post<RefreshTokenResponse>(
      API_ENDPOINTS.AUTH.REFRESH,
      { refreshToken }
    );
    return response.data;
  },

  /**
   * Get user by ID
   * Fetches full user info including phone number
   * Regular users can only access their own profile; admin/operator can access any
   * @param userId - The ID of the user to fetch
   * @returns User data
   */
  getUserById: async (userId: string): Promise<User> => {
    const response = await api.get<User>(`/users/${userId}`);
    return response.data;
  },

  /**
   * Check if a phone number is available for registration
   * @param phone - 10-digit Indian phone number
   * @returns { available: true } or throws 409 if taken
   */
  checkPhone: async (phone: string): Promise<{ available: boolean }> => {
    const response = await api.post<{ available: boolean }>(API_ENDPOINTS.AUTH.CHECK_PHONE, { phone });
    return response.data;
  },

  /**
   * Check if an email is available for registration
   * @param email - Email address
   * @returns { available: true } or throws 409 if taken
   */
  checkEmail: async (email: string): Promise<{ available: boolean }> => {
    const response = await api.post<{ available: boolean }>(API_ENDPOINTS.AUTH.CHECK_EMAIL, { email });
    return response.data;
  },

  /**
   * Delete the user's own account (soft-delete on backend)
   * Calls DELETE /users/:id which sets deletedAt timestamp
   * After success, caller is responsible for clearing auth state
   * @param userId - The ID of the user to delete
   */
  deleteAccount: async (userId: string): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },
};
